import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  Apple, Smartphone, Code2, Zap, Layers, Shield, Bell, Cloud, RefreshCw,
  ShoppingBag, CreditCard, Users, MapPin, Rocket, Palette, TrendingUp,
  Fingerprint, Gauge, Plug, Activity, FileCode, Globe,
} from "lucide-react";
import { ServicePage, type ServicePageData } from "@/components/sections/service-page";

const CHILDREN: Record<string, ServicePageData & { metaDesc: string }> = {
  "ios-swift": {
    eyebrow: "Mobile · Platforms",
    title: "iOS Development (Swift)",
    sub: "Native iOS apps built with Swift, SwiftUI and the latest Apple frameworks.",
    metaDesc: "Native iOS app development with Swift and SwiftUI — performant, polished apps for iPhone, iPad and Apple Watch.",
    intro:
      "We build native iOS apps that feel right at home on Apple platforms. SwiftUI for modern interfaces, UIKit when you need control, Combine for reactive flows, and full integration with HealthKit, ARKit, WidgetKit and the rest of the Apple ecosystem.",
    features: [
      { icon: Apple, title: "SwiftUI + UIKit", description: "Modern declarative UI with UIKit interop where it matters." },
      { icon: Zap, title: "Native performance", description: "Metal-accelerated rendering and smooth 120Hz ProMotion." },
      { icon: Fingerprint, title: "Face ID / Touch ID", description: "Biometric auth and Keychain-backed secure storage." },
      { icon: Bell, title: "APNs + Live Activities", description: "Rich notifications, Dynamic Island and Live Activities." },
      { icon: Layers, title: "Widgets + watchOS", description: "Home/Lock screen widgets and Apple Watch companions." },
      { icon: Shield, title: "App Store ready", description: "Privacy manifests, ATT, and review-pass on first submission." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "User flows, Apple HIG alignment, and platform priorities." },
      { step: "02", title: "Prototype", description: "Interactive SwiftUI prototype on real devices." },
      { step: "03", title: "Build", description: "TestFlight builds every sprint with stakeholder reviews." },
      { step: "04", title: "Launch", description: "App Store submission, ASO, and crash monitoring." },
    ],
    deliverables: ["Native iOS app (Swift)", "Full Xcode project", "TestFlight + App Store listing", "Push + analytics setup", "Crash + performance monitoring", "30 days support"],
    techStack: ["Swift 5.9", "SwiftUI", "UIKit", "Combine", "Core Data", "CloudKit", "APNs", "WidgetKit", "ARKit"],
    pricing: [
      { name: "MVP", price: "$18k", features: ["Up to 8 screens", "Auth + backend", "App Store launch", "8 weeks"] },
      { name: "Full app", price: "$45k", period: "project", highlighted: true, features: ["20+ screens", "Widgets + watchOS", "Live Activities", "12–16 weeks"] },
      { name: "Retainer", price: "$9k", period: "month", features: ["Dedicated iOS engineer", "iOS releases each sprint", "Crash + perf work"] },
    ],
    faqs: [
      { q: "SwiftUI or UIKit?", a: "SwiftUI for new apps targeting iOS 16+. UIKit for complex existing apps or screens that need fine control." },
      { q: "Do you support iPad and Watch?", a: "Yes — adaptive layouts for iPad and dedicated watchOS targets when relevant." },
    ],
  },
  "android-kotlin": {
    eyebrow: "Mobile · Platforms",
    title: "Android Development (Kotlin)",
    sub: "Native Android apps built with Kotlin, Jetpack Compose and modern Android architecture.",
    metaDesc: "Native Android app development with Kotlin and Jetpack Compose — modern, performant apps across phones, tablets and Wear OS.",
    intro:
      "We build native Android apps the modern way: Kotlin and Jetpack Compose, coroutines for async work, Hilt for clean dependency injection, and full support for Material 3, Wear OS, and the Play Store's latest policies.",
    features: [
      { icon: Smartphone, title: "Jetpack Compose", description: "Modern declarative UI with Material 3 theming." },
      { icon: Code2, title: "Clean architecture", description: "MVVM, use cases, and testable boundaries." },
      { icon: Zap, title: "Coroutines + Flow", description: "Structured concurrency and reactive streams." },
      { icon: Bell, title: "FCM notifications", description: "Targeted push, in-app messaging, and channels." },
      { icon: Layers, title: "Widgets + Wear OS", description: "Glance widgets and Wear OS companions when needed." },
      { icon: Shield, title: "Play Store ready", description: "App Bundle, target API, and data safety form." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "User flows, Material guidelines, and Play policies." },
      { step: "02", title: "Prototype", description: "Compose previews and on-device user testing." },
      { step: "03", title: "Build", description: "Per-sprint internal-track releases for stakeholders." },
      { step: "04", title: "Launch", description: "Play Store submission, ASO, and Crashlytics setup." },
    ],
    deliverables: ["Native Android app (Kotlin)", "Full Android Studio project", "Play Store listing", "Push + analytics setup", "Crash + performance monitoring", "30 days support"],
    techStack: ["Kotlin", "Jetpack Compose", "Coroutines", "Hilt", "Room", "DataStore", "FCM", "Glance", "Wear OS"],
    pricing: [
      { name: "MVP", price: "$16k", features: ["Up to 8 screens", "Auth + backend", "Play Store launch", "8 weeks"] },
      { name: "Full app", price: "$40k", period: "project", highlighted: true, features: ["20+ screens", "Widgets + Wear OS", "Offline-first", "12–16 weeks"] },
      { name: "Retainer", price: "$8k", period: "month", features: ["Dedicated Android engineer", "Releases each sprint", "Crash + perf work"] },
    ],
    faqs: [
      { q: "Compose or XML views?", a: "Compose for new apps. We can mix into existing XML codebases incrementally." },
      { q: "Do you handle Play Console submissions?", a: "Yes — we set up internal/closed/open tracks, data safety forms, and ASO." },
    ],
  },
  "react-native": {
    eyebrow: "Mobile · Platforms",
    title: "React Native Development",
    sub: "One codebase, two stores — React Native and Expo apps that feel native.",
    metaDesc: "React Native and Expo mobile app development with native performance, OTA updates, and full iOS + Android parity.",
    intro:
      "React Native lets you ship to iOS and Android from one TypeScript codebase without sacrificing feel. We use Expo for fast setup and EAS Build for production, drop into native modules when needed, and ship OTA updates so bug fixes land in minutes.",
    features: [
      { icon: Smartphone, title: "iOS + Android parity", description: "Single codebase deploys to both app stores." },
      { icon: Zap, title: "New Architecture", description: "Fabric + TurboModules for smoother, faster apps." },
      { icon: RefreshCw, title: "OTA updates", description: "Ship JS fixes in minutes via EAS Update." },
      { icon: Bell, title: "Push notifications", description: "Expo Notifications, FCM and APNs unified." },
      { icon: Cloud, title: "Offline-first sync", description: "Local-first with TanStack Query, WatermelonDB or RxDB." },
      { icon: Layers, title: "Native modules", description: "Drop into Swift / Kotlin where parity isn't enough." },
    ],
    process: [
      { step: "01", title: "Strategy", description: "Platform priorities, native module needs, OTA policy." },
      { step: "02", title: "Prototype", description: "Expo Go prototype tested on real iOS + Android devices." },
      { step: "03", title: "Build", description: "EAS Build pipeline with TestFlight + internal track each sprint." },
      { step: "04", title: "Launch", description: "Store submissions, OTA channels, and crash monitoring." },
    ],
    deliverables: ["React Native / Expo app", "EAS Build + Update pipeline", "Both store submissions", "Push + analytics setup", "Crash + perf monitoring", "30 days support"],
    techStack: ["React Native", "Expo", "TypeScript", "Reanimated", "Skia", "TanStack Query", "Zustand", "EAS Build", "Sentry"],
    pricing: [
      { name: "MVP", price: "$15k", features: ["Up to 8 screens", "Auth + backend", "Both stores", "6–8 weeks"] },
      { name: "Full product", price: "$35k", period: "project", highlighted: true, features: ["20+ screens", "Payments + push", "Offline sync", "10–14 weeks"] },
      { name: "Retainer", price: "$8k", period: "month", features: ["Dedicated team", "Bi-weekly releases", "OTA + bug fixes"] },
    ],
    faqs: [
      { q: "Expo or bare workflow?", a: "Expo + Dev Client for nearly everything. EAS handles native builds, OTA, and submissions." },
      { q: "Performance vs native?", a: "Indistinguishable for typical UIs. We drop to native for heavy graphics, AR, or specialized hardware." },
    ],
  },
  "flutter": {
    eyebrow: "Mobile · Platforms",
    title: "Flutter Development",
    sub: "Beautiful, pixel-perfect cross-platform apps with Flutter and Dart.",
    metaDesc: "Flutter and Dart cross-platform development for iOS, Android, web and desktop with native performance.",
    intro:
      "Flutter is our pick when design fidelity matters most. Pixel-perfect on every platform, 120Hz-smooth animations, and a single Dart codebase that targets iOS, Android, web and desktop — with native modules where the SDK doesn't reach.",
    features: [
      { icon: Palette, title: "Pixel-perfect UI", description: "Same Material/Cupertino UI on every device." },
      { icon: Zap, title: "Impeller renderer", description: "Smooth 120Hz animations and predictable frame times." },
      { icon: Code2, title: "Dart + Riverpod", description: "Strong types, sealed classes, and clean state management." },
      { icon: Cloud, title: "Offline-first", description: "Drift / Isar local DB with background sync." },
      { icon: Bell, title: "Push + deep links", description: "FCM, APNs, and universal/app link routing." },
      { icon: Plug, title: "Platform channels", description: "Drop into Swift / Kotlin for native capabilities." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Design system, platform priorities, native needs." },
      { step: "02", title: "Prototype", description: "DartPad / Flutter prototype tested on real devices." },
      { step: "03", title: "Build", description: "Codemagic / Fastlane CI with internal builds each sprint." },
      { step: "04", title: "Launch", description: "Store submissions, crash + performance monitoring." },
    ],
    deliverables: ["Flutter app (iOS + Android)", "Optional web/desktop build", "CI/CD with Fastlane", "Push + analytics setup", "Crash monitoring", "30 days support"],
    techStack: ["Flutter", "Dart", "Riverpod", "GoRouter", "Drift", "Firebase", "Fastlane", "Codemagic"],
    pricing: [
      { name: "MVP", price: "$15k", features: ["Up to 8 screens", "Auth + backend", "Both stores", "6–8 weeks"] },
      { name: "Full app", price: "$36k", period: "project", highlighted: true, features: ["20+ screens", "Offline sync", "Payments + push", "10–14 weeks"] },
      { name: "Retainer", price: "$8k", period: "month", features: ["Dedicated Flutter engineer", "Bi-weekly releases", "Crash + perf work"] },
    ],
    faqs: [
      { q: "Flutter or React Native?", a: "Flutter for design-heavy apps and graphics-rich UIs. RN when sharing code or libraries with a React web app." },
      { q: "Does Flutter web work in production?", a: "Yes for app-like UIs and internal tools. Not ideal as a marketing site." },
    ],
  },
  "on-demand-apps": {
    eyebrow: "Mobile · App Types",
    title: "On-demand Apps",
    sub: "Uber-style on-demand apps with realtime tracking, dispatch and payments.",
    metaDesc: "On-demand mobile apps with realtime location, dispatch logic, driver/customer flows, and integrated payments.",
    intro:
      "We build on-demand platforms end-to-end — customer app, provider/driver app, and admin dispatch dashboard. Realtime location, route optimization, ETA prediction, in-app chat, surge pricing and Stripe Connect payouts.",
    features: [
      { icon: MapPin, title: "Realtime tracking", description: "Live driver location, ETAs and route playback." },
      { icon: Users, title: "Dispatch logic", description: "Auto-assign, manual override, and zone-based routing." },
      { icon: CreditCard, title: "Split payments", description: "Stripe Connect with payouts, holds and refunds." },
      { icon: Bell, title: "Realtime updates", description: "Push, in-app and SMS at every status change." },
      { icon: Shield, title: "Driver onboarding", description: "Document upload, KYC, and background-check integrations." },
      { icon: Activity, title: "Admin dashboard", description: "Live ops view with surge, supply, and SLA monitoring." },
    ],
    process: [
      { step: "01", title: "Strategy", description: "Service model, geo zones, pricing and payouts." },
      { step: "02", title: "Prototype", description: "Three apps mapped end-to-end with state diagrams." },
      { step: "03", title: "Build", description: "Customer → provider → admin in parallel sprints." },
      { step: "04", title: "Launch", description: "Pilot zone, monitor KPIs, expand." },
    ],
    deliverables: ["Customer app (iOS + Android)", "Provider/driver app", "Admin dispatch dashboard", "Payments + payouts (Stripe Connect)", "Realtime tracking infra", "30 days support"],
    techStack: ["React Native", "TypeScript", "Mapbox / Google Maps", "Supabase Realtime", "Stripe Connect", "Twilio", "PostGIS", "OneSignal"],
    pricing: [
      { name: "MVP", price: "$28k", features: ["1 service vertical", "1 city pilot", "Stripe payouts", "10 weeks"] },
      { name: "Full platform", price: "$70k", period: "project", highlighted: true, features: ["Multi-service", "Surge + zones", "Admin + analytics", "14–18 weeks"] },
      { name: "Growth retainer", price: "$12k", period: "month", features: ["Dedicated team", "Bi-weekly releases", "Live ops support"] },
    ],
    faqs: [
      { q: "Mapbox or Google Maps?", a: "Mapbox for cost and customization. Google Maps for best directions and Places quality." },
      { q: "How do you handle scale?", a: "Geo-sharded realtime, queue-based dispatch, and per-zone autoscaling." },
    ],
  },
  "marketplace-apps": {
    eyebrow: "Mobile · App Types",
    title: "Marketplace Apps",
    sub: "Two-sided marketplaces with listings, search, payments and reviews.",
    metaDesc: "Two-sided marketplace mobile apps with listings, search, Stripe Connect payments, messaging and reviews.",
    intro:
      "We build marketplaces where buyers find what they want and sellers actually get paid — rich listings, fast search, in-app messaging, Stripe Connect payouts with holds and disputes, ratings, and the admin tools to keep the marketplace healthy.",
    features: [
      { icon: ShoppingBag, title: "Listings + media", description: "Rich listings with image/video, variants and inventory." },
      { icon: Zap, title: "Instant search", description: "Algolia / Meilisearch with filters, facets and ranking." },
      { icon: CreditCard, title: "Stripe Connect", description: "Onboarding, payouts, holds, refunds and disputes." },
      { icon: Users, title: "Messaging", description: "Buyer/seller chat with media and order context." },
      { icon: Shield, title: "Trust & safety", description: "Reviews, reports, fraud signals, and admin moderation." },
      { icon: Activity, title: "Seller dashboard", description: "Orders, payouts, analytics, and listing health." },
    ],
    process: [
      { step: "01", title: "Strategy", description: "Categories, taxonomy, fees, and supply seeding plan." },
      { step: "02", title: "Prototype", description: "Buyer + seller flows mapped end-to-end." },
      { step: "03", title: "Build", description: "Listings → checkout → messaging → admin in sprints." },
      { step: "04", title: "Launch", description: "Beta with seeded supply, then open to public." },
    ],
    deliverables: ["Buyer app (iOS + Android)", "Seller app or web dashboard", "Admin moderation tools", "Stripe Connect payouts", "Search + recommendations", "30 days support"],
    techStack: ["React Native", "TypeScript", "Algolia", "Stripe Connect", "Supabase", "PostgreSQL", "Cloudflare R2", "Sendbird / Stream"],
    pricing: [
      { name: "MVP", price: "$25k", features: ["Listings + checkout", "Basic search", "1 category", "10 weeks"] },
      { name: "Full marketplace", price: "$65k", period: "project", highlighted: true, features: ["Multi-category", "Messaging + reviews", "Admin tools", "14–18 weeks"] },
      { name: "Growth retainer", price: "$10k", period: "month", features: ["Dedicated team", "Continuous improvements", "Trust + safety work"] },
    ],
    faqs: [
      { q: "Stripe Connect Express or Custom?", a: "Express by default — fastest onboarding. Custom when you need full UI control or non-standard payouts." },
      { q: "How do you bootstrap supply?", a: "Concierge onboarding, manual listings, and seeded inventory before public launch." },
    ],
  },
  "fintech-apps": {
    eyebrow: "Mobile · App Types",
    title: "Fintech Apps",
    sub: "Banking, payments, investing and crypto apps built secure-by-default.",
    metaDesc: "Fintech mobile apps with KYC, payments, ledger, investing or crypto features — built for compliance and security.",
    intro:
      "We build fintech apps with the rigor they deserve — KYC/AML onboarding, double-entry ledgers, encrypted vaults, biometric auth, transaction signing, SOC 2-aligned infrastructure, and integrations with banking-as-a-service and card-issuing providers.",
    features: [
      { icon: Shield, title: "KYC / AML", description: "Persona, Onfido or Sumsub onboarding with risk scoring." },
      { icon: Fingerprint, title: "Biometric + 2FA", description: "Face ID, Touch ID, passkeys and TOTP enforcement." },
      { icon: CreditCard, title: "Card issuing", description: "Marqeta / Lithic / Stripe Issuing with controls and 3DS." },
      { icon: Layers, title: "Double-entry ledger", description: "Auditable balances, holds, fees and reconciliation." },
      { icon: Activity, title: "Risk + fraud", description: "Velocity rules, device fingerprinting, and Sardine/Alloy." },
      { icon: Cloud, title: "BaaS partners", description: "Unit, Treasury Prime, Bond, or direct bank rails." },
    ],
    process: [
      { step: "01", title: "Compliance scoping", description: "Licensing path, BSA/AML program, vendor selection." },
      { step: "02", title: "Architecture", description: "Ledger, vault, KYC flow, and ops dashboards." },
      { step: "03", title: "Build", description: "Security-reviewed sprints with pen-test checkpoints." },
      { step: "04", title: "Launch", description: "Closed beta, then phased rollout with monitoring." },
    ],
    deliverables: ["Customer app (iOS + Android)", "Ops + risk dashboard", "Double-entry ledger", "KYC + card issuing integrations", "Audit logs + SOC 2 evidence", "30 days support"],
    techStack: ["React Native", "Swift / Kotlin (native security)", "Postgres", "Vault", "Stripe Issuing", "Persona", "Sardine", "AWS PrivateLink"],
    pricing: [
      { name: "MVP", price: "$45k", features: ["KYC + wallet", "Single BaaS", "iOS or Android first", "12 weeks"] },
      { name: "Full app", price: "$110k", period: "project", highlighted: true, features: ["Card issuing", "Multi-product", "Risk + ops", "18–24 weeks"] },
      { name: "Compliance retainer", price: "$18k", period: "month", features: ["Dedicated team", "SOC 2 prep", "On-call + audit support"] },
    ],
    faqs: [
      { q: "Do we need our own banking license?", a: "Usually no — we integrate with BaaS partners that hold the license. We help you choose." },
      { q: "What about crypto features?", a: "Yes — custodial via Fireblocks/BitGo or non-custodial with hardware-backed key storage." },
    ],
  },
  "social-apps": {
    eyebrow: "Mobile · App Types",
    title: "Social Apps",
    sub: "Feeds, profiles, messaging and creator tools — scalable from day one.",
    metaDesc: "Social mobile apps with feeds, profiles, messaging, and moderation built to scale.",
    intro:
      "We build social apps with the parts users actually expect: a fast personalized feed, profiles that load instantly, realtime DMs, push that's not spammy, and the moderation tooling you need so the community stays one you'd want to be in.",
    features: [
      { icon: Users, title: "Feed + profiles", description: "Ranked feeds, follows, blocks, mutes and discovery." },
      { icon: Zap, title: "Realtime DMs", description: "1:1 and group messaging with media, reactions, typing." },
      { icon: Bell, title: "Smart notifications", description: "Batched, personalized, and respectful of quiet hours." },
      { icon: Shield, title: "Moderation tools", description: "Reporting, queues, auto-flagging, and shadow bans." },
      { icon: Cloud, title: "Media pipeline", description: "Image/video uploads, transcoding, CDN and thumbnails." },
      { icon: Activity, title: "Creator analytics", description: "Reach, engagement, follower growth and monetization." },
    ],
    process: [
      { step: "01", title: "Strategy", description: "Audience, content types, growth loops and norms." },
      { step: "02", title: "Prototype", description: "Feed UX, posting flow, and DM patterns on real devices." },
      { step: "03", title: "Build", description: "Auth → feed → posting → DMs → moderation in sprints." },
      { step: "04", title: "Launch", description: "Closed beta, growth loop instrumentation, scale up." },
    ],
    deliverables: ["Mobile app (iOS + Android)", "Realtime DM backend", "Media pipeline + CDN", "Moderation tools", "Analytics dashboard", "30 days support"],
    techStack: ["React Native", "TypeScript", "Stream / Sendbird", "Postgres + Redis", "Cloudflare Stream", "OneSignal", "Algolia", "PostHog"],
    pricing: [
      { name: "MVP", price: "$30k", features: ["Auth + feed", "Posting + DMs", "Basic moderation", "10 weeks"] },
      { name: "Full social", price: "$75k", period: "project", highlighted: true, features: ["Ranking + discovery", "Live + creator tools", "Advanced moderation", "14–20 weeks"] },
      { name: "Growth retainer", price: "$12k", period: "month", features: ["Dedicated team", "Growth experiments", "Trust + safety work"] },
    ],
    faqs: [
      { q: "Build chat from scratch?", a: "Use Stream/Sendbird at the start, migrate to in-house once you have scale and revenue to justify it." },
      { q: "How do you handle abuse?", a: "Multi-layered: client friction, automated detection, human queue, and per-community policy." },
    ],
  },
  "app-strategy": {
    eyebrow: "Mobile · Lifecycle",
    title: "App Strategy",
    sub: "Product, platform and roadmap decisions backed by data — before you build.",
    metaDesc: "Mobile app strategy: product discovery, platform choice, roadmap, monetization and launch planning.",
    intro:
      "Most failed apps got the strategy wrong, not the code. We run focused discovery sprints — interviews, competitive teardown, platform analysis, monetization modeling, and a phased roadmap so your first build hits the market with intent.",
    features: [
      { icon: Rocket, title: "Product discovery", description: "Interviews, JTBD framing, and opportunity scoring." },
      { icon: Smartphone, title: "Platform decisions", description: "Native vs cross-platform, iOS-first vs both, web vs app." },
      { icon: CreditCard, title: "Monetization modeling", description: "Subscription, freemium, transactional, ads — with math." },
      { icon: TrendingUp, title: "Phased roadmap", description: "MVP → growth → scale with clear success metrics." },
      { icon: Gauge, title: "Competitive teardown", description: "Feature, pricing, and UX comparison across rivals." },
      { icon: FileCode, title: "Tech architecture brief", description: "Stack, hosting, integrations, and team shape." },
    ],
    process: [
      { step: "01", title: "Interviews", description: "10–15 customer conversations to validate the core problem." },
      { step: "02", title: "Market scan", description: "Competitor teardown and category trend analysis." },
      { step: "03", title: "Strategy", description: "Roadmap, monetization model, and architecture brief." },
      { step: "04", title: "Handoff", description: "Workshop with engineering, design and leadership." },
    ],
    deliverables: ["Discovery interview synthesis", "Competitive teardown", "Phased roadmap", "Monetization model", "Architecture brief", "Stakeholder workshop"],
    techStack: ["Dovetail", "Maze", "Figma", "Notion", "Mixpanel", "App Annie / Sensor Tower"],
    pricing: [
      { name: "Sprint", price: "$8k", features: ["2-week discovery", "Roadmap + brief", "1 workshop", "Async support"] },
      { name: "Full strategy", price: "$22k", period: "project", highlighted: true, features: ["4-week engagement", "Interviews + research", "Architecture + monetization", "Exec workshop"] },
      { name: "Embedded advisor", price: "$6k", period: "month", features: ["Weekly sessions", "Roadmap stewardship", "Hiring + vendor support"] },
    ],
    faqs: [
      { q: "Do we have to build with you after?", a: "No. The strategy is yours to take to any team — we deliberately design it that way." },
      { q: "Will you do user interviews?", a: "Yes — we recruit, run, and synthesize all sessions." },
    ],
  },
  "uiux-prototyping": {
    eyebrow: "Mobile · Lifecycle",
    title: "UI/UX & Prototyping",
    sub: "App design and interactive prototypes that test ideas before code.",
    metaDesc: "Mobile UI/UX design and interactive prototyping with Figma — design systems and user-tested flows.",
    intro:
      "Great apps come from designed-then-tested ideas, not designed-then-prayed. We build interactive Figma prototypes you can hand to real users on real devices, iterate based on what they actually do, and ship a production-ready design system to engineering.",
    features: [
      { icon: Palette, title: "Design system", description: "Tokens, components, and Figma libraries for engineering." },
      { icon: Smartphone, title: "Native patterns", description: "Apple HIG and Material 3 done right, not generic." },
      { icon: Layers, title: "Interactive prototypes", description: "Figma + Maze flows installable on real devices." },
      { icon: Users, title: "Usability testing", description: "Moderated and unmoderated tests with synthesis." },
      { icon: Globe, title: "Accessibility", description: "WCAG 2.2, color contrast, screen reader and dynamic type." },
      { icon: Rocket, title: "Engineering handoff", description: "Specs, tokens, motion specs, and dev-ready Figma files." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Workflows, personas, and competitive UX reference." },
      { step: "02", title: "Wireframes", description: "Low-fi flows validated with 5–8 users." },
      { step: "03", title: "Visual + prototype", description: "High-fi design with motion and interactive prototype." },
      { step: "04", title: "Handoff", description: "Design system, tokens, and engineering walkthrough." },
    ],
    deliverables: ["Design system + tokens", "High-fi screens for every flow", "Interactive prototype", "Usability test report", "Engineering handoff package", "Motion specs"],
    techStack: ["Figma", "FigJam", "Maze", "Lottie", "Rive", "Principle"],
    pricing: [
      { name: "Prototype sprint", price: "$6k", features: ["1 core flow", "Hi-fi prototype", "5-user test", "2 weeks"] },
      { name: "Full app design", price: "$22k", period: "project", highlighted: true, features: ["End-to-end screens", "Design system", "Usability rounds", "5–7 weeks"] },
      { name: "Design retainer", price: "$5k", period: "month", features: ["Weekly design ops", "New screens", "Iteration on live data"] },
    ],
    faqs: [
      { q: "Do you do branding too?", a: "Yes — visual identity and product UI as one engagement when needed." },
      { q: "Will engineering get what they need?", a: "Yes — design tokens, Figma variables, and motion specs all dev-ready." },
    ],
  },
  "publishing-aso": {
    eyebrow: "Mobile · Lifecycle",
    title: "Publishing & ASO",
    sub: "Get to the stores, get found, and keep growing — across iOS and Android.",
    metaDesc: "App Store and Play Store publishing, App Store Optimization (ASO), and post-launch growth support.",
    intro:
      "Shipping is just the start. We handle App Store and Play Store submissions, optimize listings for the keywords your users actually search, design conversion-focused screenshots, run A/B tests on listing assets, and monitor rankings so the app keeps growing.",
    features: [
      { icon: Apple, title: "App Store submission", description: "Privacy manifest, ATT, age rating, and review pass." },
      { icon: Smartphone, title: "Play Store submission", description: "Data safety, content rating, and target API compliance." },
      { icon: TrendingUp, title: "Keyword research", description: "Sensor Tower / Data.ai-driven keyword targeting." },
      { icon: Palette, title: "Screenshot design", description: "Localized, conversion-optimized store screenshots." },
      { icon: Activity, title: "A/B testing", description: "Custom Product Pages and Play Experiments." },
      { icon: Gauge, title: "Rank monitoring", description: "Weekly keyword rank, install attribution, and reviews." },
    ],
    process: [
      { step: "01", title: "Audit", description: "Current listing review, keyword opportunities, and competitor scan." },
      { step: "02", title: "Listing build", description: "Title, subtitle, keywords, description and screenshots." },
      { step: "03", title: "Submit", description: "Both stores, including review responses and metadata fixes." },
      { step: "04", title: "Optimize", description: "Monthly tests, ranking review, and growth recommendations." },
    ],
    deliverables: ["App Store + Play Store submissions", "ASO keyword strategy", "Localized screenshots + previews", "Listing copy in target languages", "Monthly ASO report", "Review response playbook"],
    techStack: ["App Store Connect", "Play Console", "Sensor Tower", "Data.ai", "AppFollow", "Figma", "Storemaven"],
    pricing: [
      { name: "Launch", price: "$3k", features: ["Both store submissions", "Initial ASO", "Screenshot designs", "Review responses"] },
      { name: "Launch + growth", price: "$8k", period: "project", highlighted: true, features: ["Full launch", "Localized listings", "A/B tests", "60-day monitoring"] },
      { name: "ASO retainer", price: "$2.5k", period: "month", features: ["Monthly tests", "Rank tracking", "New asset variants"] },
    ],
    faqs: [
      { q: "How long does App Store review take?", a: "24–48 hours typically. We design submissions to pass on first try; >95% do." },
      { q: "Should we localize the listing?", a: "Yes — even just 5 top languages typically lifts installs 20–40%." },
    ],
  },
};

export const Route = createFileRoute("/services/mobile-application/$child")({
  component: ChildPage,
  loader: ({ params }) => {
    const entry = CHILDREN[params.child];
    if (!entry) throw notFound();
    return { entry };
  },
  head: ({ params }) => {
    const entry = CHILDREN[params.child];
    if (!entry) return {};
    const url = `/services/mobile-application/${params.child}`;
    return {
      meta: [
        { title: `${entry.title} — CoderLity` },
        { name: "description", content: entry.metaDesc },
        { property: "og:title", content: `${entry.title} — CoderLity` },
        { property: "og:description", content: entry.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
});

function ChildPage() {
  const { entry } = Route.useLoaderData();
  return <ServicePage data={entry} />;
}
